import { markUiObject as __markUi } from '@/i18n/display'
import sysRequest from '@/api/sys'

// 2026-09-03：复用平台机构扩展设置保存全端维护状态，无需新增后端接口或数据库结构。
export interface SystemMaintenanceConfig {
  enabled: boolean
  title: string
  message: string
  updatedAt?: string
  updatedBy?: string
}

interface PlatformMaintenanceSnapshot {
  config: SystemMaintenanceConfig
  orgSettings: Record<string, unknown>
  settingsValid: boolean
}

export const PLATFORM_ORG_ID = 1000
export const MAINTENANCE_ACCESS_PATH = '/maintenance-access'
export const MAINTENANCE_BYPASS_KEY = 'landiyun-maintenance-super-admin-bypass'
export const MAINTENANCE_CHANGED_EVENT = 'landiyun-maintenance-changed'
export const MAINTENANCE_BYPASS_CHANGED_EVENT = 'landiyun-maintenance-bypass-changed'

export const DEFAULT_MAINTENANCE_CONFIG: SystemMaintenanceConfig = __markUi({
  enabled: false,
  title: '系统维护中',
  message: '系统正在升级维护，为避免操作期间产生数据异常，暂时停止访问。请稍后再试，感谢您的理解。'
}, {"title":"系统维护中","message":"系统正在升级维护，为避免操作期间产生数据异常，暂时停止访问。请稍后再试，感谢您的理解。"})

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value)

const parseOrgSettings = (raw: unknown): { value: Record<string, unknown>; valid: boolean } => {
  if (raw === null || raw === undefined || raw === '') {
    return { value: {}, valid: true }
  }
  if (isPlainObject(raw)) {
    return { value: { ...raw }, valid: true }
  }
  if (typeof raw !== 'string') {
    return { value: {}, valid: false }
  }
  try {
    const parsed = JSON.parse(raw)
    return isPlainObject(parsed)
      ? { value: { ...parsed }, valid: true }
      : { value: {}, valid: false }
  } catch (_error) {
    return { value: {}, valid: false }
  }
}

export const normalizeMaintenanceConfig = (raw: unknown): SystemMaintenanceConfig => {
  const source = isPlainObject(raw) ? raw : {}
  return {
    enabled: source.enabled === true,
    title: typeof source.title === 'string' && source.title.trim()
      ? source.title.trim().slice(0, 40)
      : DEFAULT_MAINTENANCE_CONFIG.title,
    message: typeof source.message === 'string' && source.message.trim()
      ? source.message.trim().slice(0, 500)
      : DEFAULT_MAINTENANCE_CONFIG.message,
    updatedAt: typeof source.updatedAt === 'string' ? source.updatedAt : undefined,
    updatedBy: typeof source.updatedBy === 'string' ? source.updatedBy : undefined
  }
}

export const loadPlatformMaintenance = async (): Promise<PlatformMaintenanceSnapshot> => {
  const response: any = await sysRequest.getPublicPlatformInfo()
  const parsedSettings = parseOrgSettings(response?.data?.orgInfo?.settings)
  return {
    config: normalizeMaintenanceConfig(parsedSettings.value.systemMaintenance),
    orgSettings: parsedSettings.value,
    settingsValid: parsedSettings.valid
  }
}

export const savePlatformMaintenance = async (
  nextConfig: SystemMaintenanceConfig,
  updatedBy: string
): Promise<SystemMaintenanceConfig> => {
  const snapshot = await loadPlatformMaintenance()
  if (!snapshot.settingsValid) {
    throw new Error('平台扩展设置格式异常，为避免覆盖现有配置，本次未保存。')
  }
  const normalized = normalizeMaintenanceConfig({
    ...nextConfig,
    updatedAt: new Date().toISOString(),
    updatedBy
  })
  const mergedSettings = {
    ...snapshot.orgSettings,
    systemMaintenance: normalized
  }
  const response: any = await sysRequest.saveOrgSettings({
    id: PLATFORM_ORG_ID,
    settings: JSON.stringify(mergedSettings)
  })
  // 2026-09-03：只有数据库实际更新平台设置后才切换前端状态，避免零行更新被误报成功。
  if (Number(response?.data) <= 0) {
    throw new Error('维护设置未成功写入，请刷新后重试。')
  }
  return normalized
}

export const notifyMaintenanceChanged = (config: SystemMaintenanceConfig) => {
  window.dispatchEvent(new CustomEvent(MAINTENANCE_CHANGED_EVENT, { detail: config }))
}

export const setMaintenanceBypass = (config: SystemMaintenanceConfig) => {
  // 2026-09-03：绕过凭证绑定本次维护更新时间，避免旧标签页在下一轮维护中被错误放行。
  window.sessionStorage.setItem(MAINTENANCE_BYPASS_KEY, config.updatedAt || '')
  window.dispatchEvent(new Event(MAINTENANCE_BYPASS_CHANGED_EVENT))
}

export const clearMaintenanceBypass = () => {
  if (!window.sessionStorage.getItem(MAINTENANCE_BYPASS_KEY)) return
  window.sessionStorage.removeItem(MAINTENANCE_BYPASS_KEY)
  window.dispatchEvent(new Event(MAINTENANCE_BYPASS_CHANGED_EVENT))
}

export const hasMaintenanceBypass = (config: SystemMaintenanceConfig) =>
  Boolean(config.updatedAt)
  && window.sessionStorage.getItem(MAINTENANCE_BYPASS_KEY) === config.updatedAt
